import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

import { message, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_usaLow from "@amcharts/amcharts5-geodata/usaLow";

import AllocationCaseFilter from "./AllocationCaseFilter";
import AllocationPhysicianFilter from "./AllocationPhysicianFilter";
import {
  getCaseChartReports,
  getPhysicianChartReports,
} from "../../api/ReportChartsAPI";

const StyledDiv = styled.div`
  background-color: white;
  padding: 24px 28px;
`;

const StyledChartDiv = styled.div`
  width: 100%;
  height: calc(100vh - 300px);
  min-height: 400px;

  @media screen and (max-width: 1500px) {
    min-height: 350px;
  }
`;

const DEFAULT_FILL = am5.color(0xe5e5e5);
const HEAT_MIN = am5.color(0xe5e5e5);
const HEAT_MAX = am5.color(0x7bad7e);
const HOVER_FILL = am5.color(0x7bad7e);

const AllocationChart = ({ type }) => {
  const [mapData, setMapData] = useState();
  const [maxCount, setMaxCount] = useState();
  const [loading, setLoading] = useState(false);

  const rootRef = useRef(null);

  useEffect(() => {
    if (type === "physicians") {
      fetchPhysicianChartReportData();
    } else if (type === "cases") {
      fetchCaseChartReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const fetchCaseChartReportData = (filterData) => {
    setLoading(true);
    getCaseChartReports(filterData)
      .then((res) => {
        const { data } = res;
        if (data && data.payload) {
          setMapData(data.payload.mapData);
          setMaxCount(data.payload.maxCount);
        } else {
          message.error("Unable to load case report data !");
        }
      })
      .catch((error) => {
        const { response } = error;
        if (response?.data?.message) {
          message.error(response.data.message);
        } else {
          message.error("Unable to load case report data!");
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchPhysicianChartReportData = (filterData) => {
    setLoading(true);
    getPhysicianChartReports(filterData)
      .then((res) => {
        const { data } = res;
        if (data && data.payload) {
          setMapData(data.payload.mapData);
          setMaxCount(data.payload.maxCount);
        } else {
          message.error("Unable to load physician report data !");
        }
      })
      .catch((error) => {
        const { response } = error;
        if (response?.data?.message) {
          message.error(response.data.message);
        } else {
          message.error("Unable to load physician report data !");
        }
      })
      .finally(() => setLoading(false));
  };

  useLayoutEffect(() => {
    if (!mapData) return undefined;

    const root = am5.Root.new(type);
    rootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "translateX",
        panY: "translateY",
        projection: am5map.geoAlbersUsa(),
        maxZoomLevel: 62,
      })
    );

    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_usaLow,
        valueField: "value",
        calculateAggregates: true,
      })
    );
    polygonSeries.data.setAll(mapData ?? []);

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{name}: {value}",
      strokeWidth: 0.5,
      interactive: true,
      fill: DEFAULT_FILL,
    });
    polygonSeries.mapPolygons.template.states.create("hover", {
      fill: HOVER_FILL,
    });

    polygonSeries.set("heatRules", [
      {
        target: polygonSeries.mapPolygons.template,
        dataField: "value",
        min: HEAT_MIN,
        max: HEAT_MAX,
        key: "fill",
      },
    ]);

    const effectiveMax = maxCount && maxCount !== 0 ? maxCount : 1;
    const heatLegend = chart.children.push(
      am5.HeatLegend.new(root, {
        orientation: "horizontal",
        startColor: HEAT_MIN,
        endColor: HEAT_MAX,
        startText: "Little",
        endText: "A lot!",
        stepCount: 5,
        startValue: 0,
        endValue: effectiveMax,
        x: am5.percent(76),
        y: am5.percent(95),
        width: am5.percent(20),
      })
    );
    heatLegend.startLabel.setAll({ forceHidden: true });
    heatLegend.endLabel.setAll({ forceHidden: true });

    const labelSeries = chart.series.push(
      am5map.MapPointSeries.new(root, {
        polygonIdField: "id",
      })
    );
    labelSeries.bullets.push((bulletRoot) =>
      am5.Bullet.new(bulletRoot, {
        sprite: am5.Label.new(bulletRoot, {
          text: "{name}",
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p50,
          fontSize: 8,
          interactive: false,
        }),
      })
    );

    polygonSeries.events.on("datavalidated", () => {
      labelSeries.data.clear();
      polygonSeries.mapPolygons.each((polygon) => {
        const dataItem = polygon.dataItem;
        if (!dataItem) return;
        const ctx = dataItem.dataContext || {};
        const stateAbbr = (ctx.id || "").split("-").pop();
        const value = ctx.value;
        const labelText = value
          ? `${stateAbbr}: ${value}`
          : stateAbbr;
        const centroid = polygon.geoCentroid();
        if (!centroid) return;
        labelSeries.data.push({
          geometry: { type: "Point", coordinates: [centroid.longitude, centroid.latitude] },
          name: labelText,
        });
      });
    });

    chart.appear(800, 100);

    return () => {
      root.dispose();
      rootRef.current = null;
    };
  }, [type, mapData, maxCount]);

  return (
    <Spin size="large" spinning={loading} indicator={<LoadingOutlined />}>
      <StyledDiv>
        {type === "physicians" && (
          <AllocationPhysicianFilter
            fetchPhysicianChartReportData={fetchPhysicianChartReportData}
          />
        )}
        {type === "cases" && (
          <AllocationCaseFilter
            fetchCaseChartReportData={fetchCaseChartReportData}
          />
        )}

        <StyledChartDiv id={type} />
      </StyledDiv>
    </Spin>
  );
};

export default AllocationChart;
